import { useMemo, useState, useEffect } from 'react';
import {
  Chat,
  Channel,
  Window,
  ChannelHeader,
  MessageList,
  MessageInput,
  Thread,
  ChannelList,
  useCreateChatClient,
  useChatContext,
} from 'stream-chat-react';
import '../../styles/react-stream-chat/index.css';
import styles from './ChatWidget.module.css';
import './str-chat.css';
import Icons from '../Icons/Icons';
import classNames from 'classnames';

const sanitizeUser = u => {
  if (!u || !u.id) return undefined;
  return {
    id: String(u.id),
    ...(u.name != null ? { name: String(u.name) } : {}),
    ...(u.image != null ? { image: String(u.image) } : {}),
  };
};

const wrapTokenProvider = p =>
  typeof p === 'function'
    ? async () => {
        const t = await p();
        const token = t?.data?.token ?? t?.token ?? t;
        if (typeof token !== 'string') {
          throw new Error('tokenOrProvider must resolve to a string token');
        }
        return token;
      }
    : p;

const getPeerFromTransaction = (tx, myId) => {
  if (!tx || !myId) return undefined;

  const providerId = tx?.provider?.id?.uuid || tx?.provider?.id;
  const customerId = tx?.customer?.id?.uuid || tx?.customer?.id;
  const isMeCustomer = String(myId) === String(customerId);

  const peer = isMeCustomer ? tx?.provider : tx?.customer;
  const name =
    peer?.attributes?.profile?.displayName || peer?.attributes?.profile?.abbreviatedName || 'User';
  const image =
    peer?.attributes?.profile?.publicData?.avatarUrl ||
    peer?.profileImage?.variants?.default?.url ||
    undefined;

  const id = peer?.id?.uuid || peer?.id;
  return id ? { id: String(id), name, image } : undefined;
};

const EnsureDM = ({ channelId, members = [], seedText }) => {
  const { client, setActiveChannel } = useChatContext();

  const membersKey = useMemo(
    () => (Array.isArray(members) && members.length ? [...members].sort().join('|') : ''),
    [members]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!client || !membersKey) return;

      const id = channelId || membersKey.replace(/\|/g, '__');
      const membersArray = membersKey.split('|');

      const channel = client.channel('messaging', id, { members: membersArray });

      try {
        await channel.create();
      } catch (_) {}

      await channel.watch().catch(() => {});

      if (seedText && channel.state.messages.length === 0 && client.userID) {
        try {
          await channel.sendMessage({ text: seedText });
        } catch (_) {}
      }

      if (!cancelled) setActiveChannel(channel);
    })();

    return () => {
      cancelled = true;
    };
  }, [client, setActiveChannel, channelId, membersKey, seedText]);

  return null;
};

const ChatWidget = params => {
  const {
    apiKey,
    tokenOrProvider,
    user,
    transaction,
    hideList = true,
    seedText,
    filters,
    sort,
    options,
    theme = 'str-chat__theme-light',
    defaultListOpen,
    isMobileLayout,
    onNewMessage,
    toggleDrawer,
  } = params || {};

  const safeUser = useMemo(() => sanitizeUser(user), [user]);

  const peer = useMemo(() => getPeerFromTransaction(transaction, safeUser?.id), [
    transaction,
    safeUser?.id,
  ]);

  const dmMembers = useMemo(() => (safeUser?.id && peer?.id ? [safeUser.id, peer.id] : []), [
    safeUser?.id,
    peer?.id,
  ]);
  const dmChannelId = useMemo(
    () => (transaction?.id?.uuid ? `tx__${transaction.id.uuid}` : undefined),
    [transaction?.id?.uuid]
  );

  const safeProvider = useMemo(() => wrapTokenProvider(tokenOrProvider), [tokenOrProvider]);

  const client = useCreateChatClient({
    apiKey,
    tokenOrProvider: safeProvider,
    userData: safeUser,
  });

  // Listen for new messages and notify parent component
  useEffect(() => {
    if (!client || !onNewMessage) return;

    const handleMessage = event => {
      if (event.type === 'message.new' && event.message) {
        onNewMessage(event.message);
      }
    };

    client.on('message.new', handleMessage);

    return () => {
      client.off('message.new', handleMessage);
    };
  }, [client, onNewMessage]);

  const [isListOpen, setIsListOpen] = useState(() => {
    if (typeof defaultListOpen === 'boolean') return defaultListOpen;
    if (typeof window !== 'undefined') return window.innerWidth >= 768;
    return false;
  });

  const effectiveFilters = useMemo(() => {
    if (filters) return filters;
    if (dmMembers.length === 2) return { type: 'messaging', members: { $in: dmMembers } };
    return { type: 'messaging', members: { $in: [safeUser?.id] } };
  }, [filters, dmMembers, safeUser?.id]);

  if (!client) return <div className={styles.loading}>Loading chat…</div>;
  const MenuIcon = () => <Icons name="menu" />;
  const time = '10:45 AM';

  const createHeaderActions = time => {
    return function HeaderActions() {
      return (
        <div className={styles.headerActions}>
          <span className={styles.time}>{time}</span>
          <Icons name="menu" />
        </div>
      );
    };
  };
  return (
    <div className={styles.root}>
      <Chat client={client} theme={theme}>
        {dmMembers.length === 2 && (
          <EnsureDM channelId={dmChannelId} members={dmMembers} seedText={seedText} />
        )}

        {!hideList && (
          <ChannelList
            filters={effectiveFilters}
            sort={sort ?? { last_message_at: -1 }}
            options={options ?? { presence: true, state: true }}
            className={isListOpen ? 'str-chat__channel-list--open' : ''}
          />
        )}

        <Channel>
          <Window>
            {!hideList && (
              <div className={styles.headerRow}>
                <button
                  className="str-chat__header-hamburger"
                  onClick={() => setIsListOpen(v => !v)}
                  aria-label="Toggle channel list"
                >
                  <Icons name="menu" />
                </button>
                <ChannelHeader />
              </div>
            )}
            {hideList && (
              <>
                {isMobileLayout ? (
                  <div className={styles.headerRow}>
                    <button
                      className={classNames(`str-chat__header-hamburger`, styles.toggleDrawer)}
                      onClick={toggleDrawer}
                      aria-label="Toggle channel list"
                    >
                      <Icons name="leftAngle" />
                    </button>
                    <ChannelHeader MenuIcon={createHeaderActions(time)} />
                  </div>
                ) : (
                  <ChannelHeader MenuIcon={createHeaderActions(time)} />
                )}
              </>
            )}

            <MessageList />
            <MessageInput grow minRows={1} maxRows={6} additionalTextareaProps={{ rows: 2 }} />
          </Window>
          <Thread />
        </Channel>
      </Chat>
    </div>
  );
};

export default ChatWidget;
